// About.jsx — About CodeRated. What it is, why it exists, what it holds to.
//
// Modeled on the Cwave about page (Colectiv mobile/app/music/about.tsx): one
// calm page — a mark, a lede that sets the frame, then labelled sections. No
// founder bios, no timeline, no traction numbers. Mission and goals only.
//
// Renders no <nav> of its own: App.jsx:114 already mounts <Navbar /> globally,
// and the previous version added a second one, so the page shipped with two
// stacked navbars. Footer is global too (App.jsx:82), so it's not repeated here.
//
// ─────────────────────────────────────────────────────────────────────────────
// REPLACES a version that asserted things that were not true: invented
// employment history for Ross ("Former Google AI researcher") and Kayla
// ("Ex-Meta senior engineer"), a "100K+ Analyses" milestone, a 2025 "Enterprise
// Launch," and a "join thousands of developers and agencies" CTA. Those were
// claims about identifiable people and about traction. They should not come
// back without evidence.
//
// POSITIONS CONFIRMED BY KAYLA 2026-07-23 — "what we hold to" is now written from
// her actual stated commitments, not from what the product implied:
//   · you cannot pay to change a score, remove an analysis, or be re-scored faster
//   · paid tiers buy analytics on your own site + ownership of your reputation
//   · right of reply — your response publishes beside the analysis, customer or not
// These are load-bearing promises. Don't soften them without asking her.
//
// STILL OPEN — the one paragraph deliberately absent:
//   Unsolicited analysis. CodeRated crawls and publicly scores sites whose owners
//   never asked (backend/crawler/discovery.py + /feed + the outreach module). The
//   page is stronger for meeting that head-on than for leaving it unsaid, but the
//   wording has to be hers.
// ─────────────────────────────────────────────────────────────────────────────
import React from 'react';
import { Link } from 'react-router-dom';
import { ArrowRight } from 'lucide-react';

// Not marketing categories — these are the actual columns the backend scores
// and stores (ux_design, seo_fundamentals, speed_optimization, strategic_copy
// → composite score). Keep in sync with the schema.
const DIMENSIONS = [
  {
    label: 'Design',
    body: 'Whether the thing holds together — hierarchy, spacing, type, the parts that make a page feel considered rather than assembled.',
  },
  {
    label: 'Findability',
    body: 'The structural fundamentals that decide whether anyone arrives at all. Not keyword tricks — the things that are either there or missing.',
  },
  {
    label: 'Speed',
    body: 'What the page actually costs to load, on real connections. The most honest number here, and the one most often waved away.',
  },
  {
    label: 'Copy',
    body: 'Whether the writing says what the site does. Most sites lose people here, and most audits never look.',
  },
];

function SectionLabel({ children }) {
  return (
    <h2 className="text-[11px] font-bold uppercase tracking-[0.18em] text-blue-300/75 mb-3 mt-2">
      {children}
    </h2>
  );
}

function Paragraph({ children }) {
  return <p className="text-white/70 text-[15px] leading-[1.6] mb-5">{children}</p>;
}

export default function About() {
  return (
    <div className="relative z-10 min-h-screen">
      <div className="max-w-2xl mx-auto px-6 pt-16 pb-20">
        {/* The real mark — public/logo.PNG, 1024², which was sitting unused in
            the repo and referenced by nothing. An earlier draft of this page
            substituted a generic Eye glyph; this is the actual brand asset.
            The CRT-with-pixel-eye is what Navbar.jsx approximates in CSS. */}
        <div className="flex flex-col items-center pt-4 pb-10">
          <img
            src="/logo.PNG"
            alt="CodeRated"
            width={112}
            height={112}
            className="w-28 h-28 rounded-2xl shadow-lg shadow-amber-500/10"
          />
          <span className="mt-4 text-2xl font-semibold tracking-tight text-white">
            CodeRated
          </span>
        </div>

        {/* Lede carries more weight than the body — sets the frame before the
            labelled sections start. */}
        <p className="text-white/90 text-[17px] leading-[1.65] mb-10">
          CodeRated reads a website the way a good critic would, and tells you what it found.
          Not a checklist, and not a color-coded score on its own — the reasoning behind the
          number, in language you can act on. A trust layer for a web that badly needs one.
        </p>

        <SectionLabel>Why it exists</SectionLabel>
        <Paragraph>
          Feedback on a website usually arrives in one of two shapes. Free and generic —
          an automated report that flags the same forty things for every site on the
          internet, most of which don't matter for yours. Or thorough and expensive — a
          real audit from people who know what they're looking at, which takes weeks and
          prices out most of the people who need it.
        </Paragraph>
        <Paragraph>The middle is empty. That's the whole reason for this.</Paragraph>

        <SectionLabel>What it looks at</SectionLabel>
        <Paragraph>
          Four dimensions, scored separately, because a site that loads fast and reads
          badly has a different problem than one that reads well and nobody can find.
          Collapsing those into a single number hides the thing you needed to know.
        </Paragraph>
        <div className="mb-6 space-y-4">
          {DIMENSIONS.map((d) => (
            <div key={d.label} className="border-l-2 border-blue-400/30 pl-4">
              <div className="text-white/90 text-sm font-semibold mb-1">{d.label}</div>
              <div className="text-white/60 text-[14px] leading-[1.55]">{d.body}</div>
            </div>
          ))}
        </div>

        <SectionLabel>Measured, and judged</SectionLabel>
        <Paragraph>
          Two of those four are facts. Speed and findability are things we check: we fetch
          the page, time it, and look for the structural pieces that decide whether anyone
          finds you. Same page, same answer, every time — and every number comes with the
          measurement behind it.
        </Paragraph>
        <Paragraph>
          Design and copy are judgment. We say so. They're marked differently, they're
          scored separately, and they never get averaged into the measured ones to make an
          opinion look like a fact.
        </Paragraph>

        <SectionLabel>What we hold to</SectionLabel>
        <Paragraph>
          <strong className="text-white/90">You cannot pay us to change your score.</strong>{' '}
          Not to raise it, not to remove an analysis, not to be re-scored faster or more
          gently. There is no price for that here, and there won't be. A rating that can be
          bought isn't a rating, and everyone else's score would be worth less the moment
          yours was for sale.
        </Paragraph>
        <Paragraph>
          What you can pay for is understanding your own work and owning your reputation:
          full analytics on your site, history so you can see whether you're improving,
          alerts when something regresses, and a verified profile you control.
        </Paragraph>
        <Paragraph>
          And anyone can talk back. Every analysis is open for comment — you don't have to
          pay, and you don't have to prove you own the site to say we got it wrong.
          Verifying ownership gets your reply a badge, so readers know it's coming from
          the people who built the thing. It doesn't buy you permission to speak, because
          that was never ours to sell. We'd rather be argued with in public than quietly
          delete the argument.
        </Paragraph>
        <Paragraph>
          A score with no reasoning attached is a verdict, not a critique, and verdicts
          don't help anyone improve anything. If a finding can't be explained, it doesn't
          ship. The point isn't to be harsh, and it isn't to be kind — it's to be specific
          enough that you know what to do on Monday.
        </Paragraph>

        <SectionLabel>Part of Colectiv</SectionLabel>
        <Paragraph>
          CodeRated is built by the team behind{' '}
          <a
            href="https://colectiv.ai"
            target="_blank"
            rel="noopener noreferrer"
            className="text-blue-300 hover:text-blue-200 underline underline-offset-2 transition-colors"
          >
            Colectiv
          </a>
          , and shares its design language and its account. It stands on its own — you
          don't need one to use the other — but they're made by the same people, to the
          same standard, and what each one learns makes the other better.
        </Paragraph>

        {/* CTA, deliberately quiet. The old page closed on "join thousands of
            developers and agencies" — a traction claim wearing CTA clothing. */}
        <div className="mt-12 pt-10 border-t border-white/10 flex flex-col sm:flex-row gap-3">
          <Link
            to="/search"
            className="bg-gradient-to-r from-blue-500 to-cyan-500 text-white px-6 py-3 rounded-xl font-semibold text-sm hover:from-blue-400 hover:to-cyan-400 transition-all flex items-center justify-center gap-2"
          >
            <span>Analyze a site</span>
            <ArrowRight className="w-4 h-4" />
          </Link>
          <Link
            to="/feed"
            className="border border-white/15 text-white/90 px-6 py-3 rounded-xl font-semibold text-sm hover:bg-white/5 transition-all flex items-center justify-center gap-2"
          >
            <span>Browse analyses</span>
          </Link>
        </div>
      </div>
    </div>
  );
}
